// 📁 src/pages/AdminReportPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection, doc, getDoc, onSnapshot, orderBy, query,
  updateDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const AdminReportPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDebug, setShowDebug] = useState(false);
  const navigate = useNavigate();

  const auth = getAuth();
  const currentUser = auth.currentUser;

  const adminName = useMemo(() => {
    if (!currentUser) return "Admin";
    return currentUser.displayName || currentUser.email || currentUser.uid;
  }, [currentUser]);

  const fmt = (ts) => {
    if (!ts) return "—";
    try {
      if (typeof ts?.toDate === "function") return ts.toDate().toLocaleString();
      if (ts?._seconds) return new Date(ts._seconds * 1000).toLocaleString();
      return "—";
    } catch {
      return "—";
    }
  };

  // Enrich report with listing info if missing
  const enrichReport = async (r) => {
    if (!r?.listingId) return r;
    const missingTitle = !r.listingTitle;
    const missingImg = !r.listingImageURL;

    if (!missingTitle && !missingImg) return r;
    try {
      const snap = await getDoc(doc(db, "listings", r.listingId));
      if (snap.exists()) {
        const L = snap.data();
        return {
          ...r,
          listingTitle: L?.title || r.listingTitle || "",
          listingImageURL:
            Array.isArray(L?.mediaImageNames) ? L.mediaImageNames[0] || "" : "",
          listingCategory: r.listingCategory || L?.categoryMain || L?.category || "—",
        };
      }
    } catch (e) {
      console.warn("⚠️ enrichReport failed:", e);
    }
    return r;
  };

  useEffect(() => {
    const qy = query(collection(db, "reports"), orderBy("timestamp", "desc"));
    const unsub = onSnapshot(
      qy,
      async (snap) => {
        let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items = await Promise.all(items.map(enrichReport));

        if (currentUser?.uid) {
          const uid = currentUser.uid;
          await Promise.all(
            items.map(async (r) => {
              if (!r?.seenBy || !r.seenBy[uid]) {
                try {
                  await updateDoc(doc(db, "reports", r.id), {
                    [`seenBy.${uid}`]: {
                      name: adminName,
                      seenAt: serverTimestamp(),
                    },
                  });
                } catch {}
              }
            })
          );
        }

        console.log("✅ Reports fetched:", items);
        setReports(items);
        setLoading(false);
      },
      (err) => {
        console.error("❌ onSnapshot(reports) error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [adminName, currentUser]);

  // Firestore actions
  const updateStatus = (id, status) =>
    updateDoc(doc(db, "reports", id), { status });
  const addAdminNote = (id, note) =>
    updateDoc(doc(db, "reports", id), { adminNote: note });
  const assignToMe = async (id) => {
    if (!currentUser) return;
    await updateDoc(doc(db, "reports", id), {
      assignedTo: {
        uid: currentUser.uid,
        name: adminName,
        assignedAt: serverTimestamp(),
      },
      status: "in_review",
    });
  };
  const resolveReport = async (id) => {
    if (!currentUser) return;
    await updateDoc(doc(db, "reports", id), {
      status: "resolved",
      resolvedBy: {
        uid: currentUser.uid,
        name: adminName,
        resolvedAt: serverTimestamp(),
      },
    });
  };
  const blockUser = async (userId) => {
    if (!userId) return alert("No reported user ID.");
    if (window.confirm(`Block user ${userId}? This cannot be undone.`)) {
      await updateDoc(doc(db, "users", userId), { isBlocked: true });
      alert(`User ${userId} has been blocked.`);
    }
  };
  const deleteReport = (id) =>
    window.confirm("Delete this report?") && deleteDoc(doc(db, "reports", id));

  const SeenByChips = ({ seenBy }) => {
    if (!seenBy || typeof seenBy !== "object") return null;
    const entries = Object.entries(seenBy);
    if (!entries.length) return null;
    return (
      <div style={styles.seenWrap}>
        <div style={styles.seenLabel}>👀 Seen by:</div>
        <div style={styles.seenChips}>
          {entries.map(([uid, info]) => (
            <span key={uid} style={styles.seenChip}>
              <strong>{info?.name || uid}</strong>
              <span style={styles.seenTime}> · {fmt(info?.seenAt)}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  if (loading) return <div style={styles.loading}>Loading reports…</div>;

  return (
    <div style={styles.container}>
      <div style={styles.topbar}>
        <button
          onClick={() => navigate("/dashboard")}
          style={styles.backButton}
        >
          ← Back to Dashboard
        </button>
        <button
          onClick={() => setShowDebug((s) => !s)}
          style={styles.debugBtn}
        >
          {showDebug ? "Hide Debug" : "Show Debug"}
        </button>
      </div>

      <h2 style={styles.header}>🚩 User Reports</h2>

      {reports.length === 0 ? (
        <p>No reports found.</p>
      ) : (
        reports.map((r) => {
          const created = fmt(r.timestamp || r.createdAt);
          const listingImageURL = r.listingImageURL || "";

          return (
            <div key={r.id} style={styles.card}>
              <div style={styles.row}>
                {listingImageURL ? (
                  <img
                    src={listingImageURL}
                    alt="listing"
                    style={styles.thumbnail}
                  />
                ) : (
                  <div style={styles.thumbnailPlaceholder} />
                )}

                <div style={{ flex: 1 }}>
                  <p style={styles.line}>
                    <strong>📄 Listing:</strong>{" "}
                    {r.listingTitle || "—"} ({r.listingCategory || "—"})
                  </p>
                  <p style={styles.line}>
                    <strong>🔗 Listing ID:</strong> {r.listingId || "—"}
                  </p>
                  <p style={styles.line}>
                    <strong>🕒 Created:</strong> {created}
                  </p>

                  <p style={styles.line}>
                    <strong>📌 Reason:</strong> {r.reason || "—"}
                  </p>
                  <p style={styles.line}>
                    <strong>📝 Details:</strong> {r.details || "—"}
                  </p>
                  <p style={styles.line}>
                    <strong>👤 Reported User:</strong>{" "}
                    {r.reportedUserId || "—"}
                  </p>
                  <p style={styles.line}>
                    <strong>📨 Reported By:</strong>{" "}
                    {r.reportedById || r.reportedBy || "—"}
                  </p>
                  <p style={styles.line}>
                    <strong>📊 Status:</strong> {r.status || "pending"}
                  </p>

                  {r.assignedTo && (
                    <p style={styles.line}>
                      <strong>🧑‍💼 Assigned To:</strong>{" "}
                      {r.assignedTo.name || r.assignedTo.uid} ·{" "}
                      {fmt(r.assignedTo.assignedAt)}
                    </p>
                  )}
                  {r.resolvedBy && (
                    <p style={styles.line}>
                      <strong>✔️ Resolved By:</strong>{" "}
                      {r.resolvedBy.name || r.resolvedBy.uid} ·{" "}
                      {fmt(r.resolvedBy.resolvedAt)}
                    </p>
                  )}
                  <SeenByChips seenBy={r.seenBy} />
                </div>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  onClick={() => updateStatus(r.id, "reviewed")}
                  style={styles.buttonGreen}
                >
                  ✅ Mark Reviewed
                </button>
                <button
                  onClick={() => assignToMe(r.id)}
                  style={styles.buttonBlue}
                >
                  🏷 Assign to Me
                </button>
                <button
                  onClick={() => resolveReport(r.id)}
                  style={styles.buttonGray}
                >
                  ✔️ Resolve
                </button>
                <button
                  onClick={() => blockUser(r.reportedUserId)}
                  style={styles.buttonOrange}
                >
                  🚫 Block User
                </button>
                <button
                  onClick={() => deleteReport(r.id)}
                  style={styles.buttonRed}
                >
                  🗑 Delete
                </button>
              </div>

              <div style={styles.noteSection}>
                <label>
                  <strong>🛠 Admin Note:</strong>
                </label>
                <input
                  type="text"
                  defaultValue={r.adminNote || ""}
                  onBlur={(e) => addAdminNote(r.id, e.target.value)}
                  placeholder="Optional internal note"
                  style={styles.noteInput}
                />
              </div>

              {showDebug && (
                <pre style={styles.debugBox}>
                  {JSON.stringify(r, null, 2)}
                </pre>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

// --- Styles ---
const styles = {
  container: {
    padding: "2rem",
    fontFamily: "system-ui, sans-serif",
    backgroundColor: "#f9f9f9",
    minHeight: "100vh",
  },
  topbar: { display: "flex", alignItems: "center", gap: 12, marginBottom: 8 },
  backButton: {
    backgroundColor: "#eeeeee",
    border: "1px solid #ccc",
    padding: "0.4rem 0.8rem",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: 500,
  },
  debugBtn: {
    background: "#f3f4f6",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "4px 10px",
    cursor: "pointer",
    marginLeft: "auto",
  },
  header: { fontSize: "1.5rem", marginBottom: "1rem", color: "#065f46" },
  card: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "1rem",
    marginBottom: "1rem",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  },
  row: { display: "flex", gap: "1rem" },
  line: { margin: "2px 0" },
  thumbnail: {
    width: 80,
    height: 80,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #eee",
  },
  thumbnailPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 8,
    background: "#eee",
    border: "1px solid #eee",
  },
  buttonGroup: {
    marginTop: "0.75rem",
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
  },
  buttonGreen: {
    backgroundColor: "#2e7d32",
    color: "white",
    padding: "0.4rem 0.8rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  buttonBlue: {
    backgroundColor: "#2563eb",
    color: "white",
    padding: "0.4rem 0.8rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  buttonGray: {
    backgroundColor: "#616161",
    color: "white",
    padding: "0.4rem 0.8rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  buttonRed: {
    backgroundColor: "#c62828",
    color: "white",
    padding: "0.4rem 0.8rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  buttonOrange: {
    backgroundColor: "#ef6c00",
    color: "white",
    padding: "0.4rem 0.8rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  noteSection: { marginTop: "0.75rem" },
  noteInput: {
    width: "100%",
    padding: "0.5rem",
    fontSize: "0.95rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    marginTop: "0.25rem",
  },
  loading: { padding: "2rem", fontSize: "1rem" },
  seenWrap: {
    marginTop: "0.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  seenLabel: { fontWeight: 600, color: "#374151" },
  seenChips: { display: "flex", gap: "0.5rem", flexWrap: "wrap" },
  seenChip: {
    background: "#eef2ff",
    color: "#3730a3",
    padding: "0.2rem 0.5rem",
    borderRadius: "999px",
    border: "1px solid #c7d2fe",
    fontSize: "0.85rem",
  },
  seenTime: { color: "#6b7280", fontWeight: 400 },
  debugBox: {
    marginTop: 10,
    padding: 10,
    background: "#0b1021",
    color: "#d1e7ff",
    borderRadius: 6,
    overflowX: "auto",
  },
};

export default AdminReportPanel;
